import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { ApiEnvelope } from './types';

export class ApiError extends Error {
  code: number;
  data: unknown;

  constructor(code: number, message: string, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.data = data;
  }
}

type EnvelopeLike = {
  code?: number;
  msg?: string;
  message?: string;
  data?: unknown;
};

function isFetchBaseQueryError(err: unknown): err is FetchBaseQueryError {
  return Boolean(err && typeof err === 'object' && 'status' in err);
}

/** Normalize RTK Query / ApiError into a single shape. */
export function getIndexerErrorInfo(err: unknown): {
  code: number;
  message: string;
  data: unknown;
} {
  if (err instanceof ApiError) {
    return {
      code: err.code,
      message: err.message,
      data: err.data,
    };
  }

  if (isFetchBaseQueryError(err)) {
    const status =
      typeof err.status === 'number'
        ? err.status
        : err.status === 'FETCH_ERROR'
          ? 0
          : 500;
    const body = err.data as EnvelopeLike | undefined;
    if (body && typeof body === 'object') {
      const payload =
        'data' in body && (body as ApiEnvelope<unknown>).msg !== undefined
          ? body.data
          : body;
      return {
        code: typeof body.code === 'number' ? body.code : status,
        message: body.msg || body.message || 'خطای درخواست',
        data: payload ?? null,
      };
    }
    return { code: status, message: 'خطای درخواست', data: err.data ?? null };
  }

  if (err instanceof Error) {
    return { code: 500, message: err.message, data: null };
  }

  return { code: 500, message: 'خطای درخواست', data: null };
}

function payloadObject(err: unknown): Record<string, unknown> | null {
  const { data } = getIndexerErrorInfo(err);
  if (!data || typeof data !== 'object') return null;
  return data as Record<string, unknown>;
}

export function invalidUrlsFromError(err: unknown): string[] {
  const data = payloadObject(err);
  if (!data) return [];
  const urls = [
    ...(Array.isArray(data.invalidUrls)
      ? data.invalidUrls.filter((u): u is string => typeof u === 'string')
      : []),
    ...(Array.isArray(data.wrongDomainUrls)
      ? data.wrongDomainUrls.filter((u): u is string => typeof u === 'string')
      : []),
  ];
  return [...new Set(urls)];
}

export function projectHostFromError(err: unknown): string | null {
  const data = payloadObject(err);
  return typeof data?.projectHost === 'string' ? data.projectHost : null;
}

export function jobIdFromError(err: unknown): string | null {
  const data = payloadObject(err);
  return typeof data?.jobId === 'string' ? data.jobId : null;
}
